import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { toDate } from '~/lib/timestamps';
import type { Client, Invoice, Project, Quote } from '~/types/models';

export function useClientWorkspaceFinance(options: {
    client: MaybeRefOrGetter<Client | null>;
    projects: MaybeRefOrGetter<Project[]>;
    quotes: MaybeRefOrGetter<Quote[]>;
    invoices: MaybeRefOrGetter<Invoice[]>;
}) {
    const currentYear = computed(() => new Date().getFullYear());
    const clientCurrency = computed(() => (
        String(toValue(options.client)?.lifecycle?.annualValueCurrency || '').trim()
        || String(toValue(options.invoices)[0]?.currency || '').trim()
        || String(toValue(options.quotes)[0]?.currency || '').trim()
        || 'GBP'
    ));
    const clientOptions = computed(() => {
        const client = toValue(options.client);
        return client ? [{ id: client.id, name: client.name, lifecycleStatus: client.lifecycle?.status }] : [];
    });
    const projectOptions = computed(() => toValue(options.projects).map(project => ({
        id: project.id,
        name: project.name,
        clientId: project.clientId,
    })));
    const openQuotes = computed(() => toValue(options.quotes).filter(quote => ['draft', 'sent'].includes(String(quote.status || '').toLowerCase())));
    const openInvoices = computed(() => toValue(options.invoices).filter(invoice => invoice.status !== 'paid' && Number(invoice.amountDue || 0) > 0));
    const revenueInMotion = computed(() => (
        openQuotes.value.reduce((total, quote) => total + Number(quote.amountTotal || 0), 0)
        + openInvoices.value.reduce((total, invoice) => total + Number(invoice.amountDue || 0), 0)
    ));
    const invoicesThisYear = computed(() => toValue(options.invoices).filter((invoice) => {
        const date = toDate(invoice.issueDate) || toDate(invoice.paidAt) || toDate(invoice.createdAt);
        return date?.getFullYear() === currentYear.value;
    }));
    const quotesThisYear = computed(() => toValue(options.quotes).filter((quote) => {
        const date = toDate(quote.issueDate) || toDate(quote.acceptedAt) || toDate(quote.createdAt);
        return date?.getFullYear() === currentYear.value;
    }));
    const revenueCollectedThisYear = computed(() => invoicesThisYear.value.reduce((total, invoice) => (
        total + Number(invoice.amountPaid || (invoice.status === 'paid' ? invoice.amountTotal : 0) || 0)
    ), 0));
    const outstandingThisYear = computed(() => invoicesThisYear.value.reduce((total, invoice) => total + Number(invoice.amountDue || 0), 0));
    const pipelineValueThisYear = computed(() => quotesThisYear.value
        .filter(quote => !['rejected', 'expired'].includes(String(quote.status || '').toLowerCase()))
        .reduce((total, quote) => total + Number(quote.amountTotal || 0), 0));
    const clientWorthThisYear = computed(() => {
        const target = Number(toValue(options.client)?.lifecycle?.annualValueTarget || 0);
        return target > 0 ? target : revenueCollectedThisYear.value + outstandingThisYear.value + pipelineValueThisYear.value;
    });
    const wonRevenueThisYear = computed(() => revenueCollectedThisYear.value + outstandingThisYear.value);

    return {
        currentYear,
        clientCurrency,
        clientOptions,
        projectOptions,
        openQuotes,
        openInvoices,
        revenueInMotion,
        revenueCollectedThisYear,
        outstandingThisYear,
        clientWorthThisYear,
        wonRevenueThisYear,
    };
}
